import { http } from '@frontend/fetch';

/**
 * Backend HTTP error structure for Bulk Messaging errors.
 */
type BulkError = {
  cause?: string;
  messages?: string[];
  code?: number;
  tags?: Array<Record<string, unknown>> | Record<string, unknown>;
};

/**
 * Gets the error cause from a bulk-v2 error.
 *
 * Example bulk-v2 error message format:
 * `{"cause":"location is not registered with 10 dlc","messages":["[gRPC] "],"code":9,"tags":[{"campaignId":"..."}]}`
 *
 * @param error - The error containing a cause string to parse
 * @returns The formatted error cause or undefined if parsing fails
 */
export const getBulkErrorCause = (error: unknown) => {
  if (!http.isHttpError(error) || !error.data) {
    return undefined;
  }

  const hasMessage = (data: unknown): data is { message: unknown } => {
    return typeof data === 'object' && data !== null && 'message' in data;
  };

  if (!hasMessage(error.data) || typeof error.data.message !== 'string') {
    return undefined;
  }

  try {
    const bulkError = JSON.parse(error.data.message) as BulkError;

    if (!bulkError.cause || typeof bulkError.cause !== 'string' || bulkError.cause.trim() === '') {
      return undefined;
    }

    const trimmedCause = bulkError.cause.trim();
    const capitalizedCause = trimmedCause.charAt(0).toUpperCase() + trimmedCause.slice(1);
    const formattedCause = capitalizedCause.endsWith('.') ? capitalizedCause : `${capitalizedCause}.`;
    return formattedCause;
  } catch {
    return undefined;
  }
};
